<template>
  <div class="news-page">
    <div class="page-header">
      <h1>News</h1>
      <div class="header-controls">
        <button class="add-btn" @click="openAddModal">+ Add News</button>
      </div>
    </div>

    <div class="search-filters">
      <div class="search-group">
        <label>Search Headline/Summary:</label>
        <input v-model="searchText" type="text" placeholder="Search headline or summary..." @input="filterNews">
      </div>
      <div class="search-group">
        <label>Date From:</label>
        <Calendar v-model="dateFrom" @date-select="filterNews" placeholder="Select start date" />
      </div>
      <div class="search-group">
        <label>Date To:</label>
        <Calendar v-model="dateTo" @date-select="filterNews" placeholder="Select end date" />
      </div>
      <button class="clear-btn" @click="clearFilters">Clear</button>
    </div>

    <div class="month-pagination">
      <button class="month-nav-btn" @click="previousMonth">&lt;</button>
      <span class="current-month">{{ currentMonthYear }}</span>
      <button class="month-nav-btn" @click="nextMonth">&gt;</button>
    </div>

    <DataTable 
      :value="filteredNews" 
      :paginator="true" 
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      :loading="false"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      :globalFilterFields="['headline']"
      v-model:filters="filters"
      filterDisplay="menu"
    >
      <template #header>
        <div class="table-header">
          <h3>News ({{ filteredNews.length }})</h3>
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Search news..." />
          </IconField>
        </div>
      </template>
      <Column field="headline" header="Headline" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search by headline" />
        </template>
      </Column>
      <!-- <Column field="summary" header="Summary" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search summary" />
        </template>
      </Column>
      <Column field="news_source" header="News Source" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search source" />
        </template>
      </Column>
      <Column field="writer" header="Writer" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search writer" />
        </template>
      </Column>
      <Column field="proposed_action" header="Proposed Action" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search action" />
        </template>
      </Column> -->
      <Column field="date_published" header="Date Published" sortable></Column>
      <Column field="news_link" header="News Link">
        <template #body="{ data }">
          <a :href="data.news_link" target="_blank">Link</a>
        </template>
      </Column>
      <Column header="Actions" :exportable="false">
        <template #body="{ data }">
          <div class="actions">
            <Button 
              icon="pi pi-pencil" 
              severity="info" 
              size="small"
              @click="openEditModal(data)" 
            />
            <Button 
              icon="pi pi-trash" 
              severity="danger" 
              size="small"
              @click="deleteNews(data.id)" 
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog 
      v-model:visible="showModal" 
      :header="isEditing ? 'Edit News' : 'Add News'" 
      :modal="true" 
      :closable="false" 
      :dismissable-mask="false"
      :style="{width: '800px'}"
    >
      <form @submit.prevent="saveNews" class="modal-form">
        <div v-if="!isEditing" class="multi-row-container">
          <div v-for="(row, idx) in formRows" :key="idx" class="form-row">
            <div class="form-row-fields">
              <div class="form-group flex-2">
                <IftaLabel>
                  <InputText 
                    :id="'headline_' + idx"
                    v-model="row.headline" 
                    :class="{ 'p-invalid': !row.headline && formSubmitted }"
                    required 
                  />
                  <label :for="'headline_' + idx">Headline *</label>
                </IftaLabel>
              </div>
              <div class="form-group flex-1">
                <IftaLabel>
                  <DatePicker 
                    :id="'date_published_' + idx"
                    v-model="row.date_published" 
                    :class="{ 'p-invalid': !row.date_published && formSubmitted }"
                    dateFormat="yy-mm-dd"
                    @date-select="selectDate"
                    required 
                  />
                  <label :for="'date_published_' + idx">Date Published *</label>
                </IftaLabel>
              </div>
              <div class="form-group flex-2">
                <IftaLabel>
                  <InputText 
                    :id="'news_link_' + idx"
                    v-model="row.news_link" 
                    :class="{ 'p-invalid': !row.news_link && formSubmitted }"
                    required 
                  />
                  <label :for="'news_link_' + idx">News Link *</label>
                </IftaLabel>
              </div>
              <Button 
                v-if="formRows.length > 1"
                type="button" 
                icon="pi pi-trash" 
                severity="danger" 
                size="small"
                text
                @click="formRows.splice(idx, 1)" 
              />
            </div>
          </div>
          <Button type="button" label="+ Add Another Row" icon="pi pi-plus" severity="secondary" size="small" @click="formRows.push({ headline: '', date_published: '', news_link: '' })" class="add-row-btn" />
        </div>

        <div v-else class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="headline"
                v-model="form.headline" 
                :class="{ 'p-invalid': !form.headline && formSubmitted }"
                required 
              />
              <label for="headline">Headline *</label>
            </IftaLabel>
          </div>
          <div class="form-group">
            <IftaLabel>
              <DatePicker 
                id="date_published"
                v-model="form.date_published" 
                :class="{ 'p-invalid': !form.date_published && formSubmitted }"
                dateFormat="yy-mm-dd"
                @date-select="selectDate"
                required 
              />
              <label for="date_published">Date Published *</label>
            </IftaLabel>
          </div>
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="news_link"
                v-model="form.news_link" 
                :class="{ 'p-invalid': !form.news_link && formSubmitted }"
                required 
              />
              <label for="news_link">News Link *</label>
            </IftaLabel>
          </div>
        </div>
        
        <div class="modal-actions">
          <Button 
            type="button" 
            label="Cancel" 
            severity="secondary" 
            icon="pi pi-times"
            @click="closeModal" 
          />
          <Button 
            type="submit" 
            :label="isEditing ? 'Update News' : 'Create News'" 
            icon="pi pi-check"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Swal from 'sweetalert2'
import api from '../axios.js'
import moment from 'moment-timezone';

const newsList = ref([])
const filteredNews = ref([])
const searchText = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const formSubmitted = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  headline: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  // summary: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  // news_source: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  // writer: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  // proposed_action: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})
const form = ref({
  id: null,
  headline: '',
  date_published: '',
  news_link: ''
})

const formRows = ref([{ headline: '', date_published: '', news_link: '' }])

const currentMonthYear = computed(() => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December']
  return `${months[currentMonth.value]} ${currentYear.value}`
});

const selectDate = () => {
    Date.prototype.toJSON = function(){ return moment(this).format(); }
    moment().tz("Asia/Taipei").format();
}

const loadNews = async () => {
  try {
    const response = await api.get('/news')
    newsList.value = response.data
    filterNews()
  } catch (error) {
    console.error('Error loading news:', error)
    newsList.value = []
    filteredNews.value = []
  }
}

const filterNews = () => {

  let filtered = [...newsList.value]
  
  // Filter by current month/year
  filtered = filtered.filter(news => {
    
  const newsDate = new Date(news.date_published)
    return newsDate.getMonth() === currentMonth.value && 
           newsDate.getFullYear() === currentYear.value
  });

  // Filter by search text (headline and summary)
  if (searchText.value) {
    const search = searchText.value.toLowerCase()
    filtered = filtered.filter(news => 
      news.headline.toLowerCase().includes(search) ||
      news.summary.toLowerCase().includes(search)
    )
  }
  
  // Filter by date range
  if (dateFrom.value) {
    
    const fromDate = dateFrom.value instanceof Date ? moment(dateFrom.value).format('YYYY-MM-DD') : dateFrom.value
   
    filtered = filtered.filter(news => news.date_published >= fromDate)
  }

  if (dateTo.value) {
    const toDate = dateTo.value instanceof Date ? moment(dateTo.value).format('YYYY-MM-DD') : dateTo.value
    filtered = filtered.filter(news => news.date_published <= toDate)
  }
  
  // Sort by recent news (date_published descending)
  filtered.sort((a, b) => new Date(b.date_published) - new Date(a.date_published))
  
  filteredNews.value = filtered
}

const clearFilters = () => {
  searchText.value = ''
  dateFrom.value = ''
  dateTo.value = ''
  filterNews()
}

const previousMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
  filterNews()
}

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
  filterNews()
}

const openAddModal = () => {
  isEditing.value = false
  formRows.value = [{ headline: '', date_published: '', news_link: '' }]
  form.value = { id: null, headline: '', date_published: '', news_link: '' }
  showModal.value = true
}

const openEditModal = (news) => {
  isEditing.value = true
  form.value = { ...news }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formSubmitted.value = false
  saving.value = false
  form.value = { id: null, headline: '', date_published: '', news_link: '' }
  formRows.value = [{ headline: '', date_published: '', news_link: '' }]
}

const saveNews = async () => {
  formSubmitted.value = true
  
  if (isEditing.value) {
    if (!form.value.headline || !form.value.date_published || !form.value.news_link) return
  } else {
    const hasInvalid = formRows.value.some(row => !row.headline || !row.date_published || !row.news_link)
    if (hasInvalid) return
  }
  
  const result = await Swal.fire({
    title: 'Confirm Action',
    text: 'Confirming',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    target: document.body
  })
  
  if (!result.isConfirmed) return
  
  try {
    saving.value = true
    
    if (isEditing.value) {
      await api.put(`/news/${form.value.id}`, {
        headline: form.value.headline,
        date_published: form.value.date_published,
        news_link: form.value.news_link
      })
    } else {
      // Create multiple rows
      for (const row of formRows.value) {
        await api.post('/news', {
          headline: row.headline,
          date_published: row.date_published,
          news_link: row.news_link
        })
      }
    }
    
    closeModal()
    loadNews()
    
    Swal.fire({
      title: 'Success!',
      text: isEditing.value ? 'News updated successfully' : `${formRows.value.length} news item(s) created successfully`,
      icon: 'success',
      confirmButtonText: 'OK',
      target: document.body
    })
  } catch (error) {
    console.error('Error saving news:', error)
  } finally {
    saving.value = false
  }
}

const deleteNews = async (id) => {
  const result = await Swal.fire({
    title: 'Delete News',
    text: 'Are you sure you want to delete this news?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/news/${id}`)
      loadNews()
      
      Swal.fire({
        title: 'Deleted!',
        text: 'News has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting news:', error)
    }
  }
}

onMounted(() => {
  loadNews()
})
</script>

<style scoped>
.news-page {
  padding: 2rem;
  /* max-width: 1400px; */
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.search-filters {
  display: flex;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.search-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.search-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e3a8a;
}

.search-group input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 200px;
}

.search-group input:focus {
  outline: none;
  border-color: #1e3a8a;
  box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
}

.clear-btn {
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  height: fit-content;
  transition: background 0.3s;
}

.clear-btn:hover {
  background: #4b5563;
}

.month-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.1);
}

.month-nav-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.month-nav-btn:hover {
  background: #1e40af;
  transform: scale(1.1);
}

.current-month {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e3a8a;
  min-width: 200px;
  text-align: center;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-calendar, .p-datepicker {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group .p-calendar .p-inputtext {
  width: 100% !important;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.multi-row-container {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: #f9fafb;
}

.form-row-fields {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.form-row-fields .flex-1 { flex: 1; }
.form-row-fields .flex-2 { flex: 2; }

.add-row-btn {
  align-self: flex-start;
}

@media (max-width: 768px) {
  .news-page {
    padding: 1rem;
  }
  
  .search-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-group input {
    min-width: auto;
  }
  

}
</style>